import * as preact from "preact";

import { postNavigate } from "postNavigate";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as ttsVoiceCatalog from "shared/ttsVoiceCatalog";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import * as urlUtils from "urlUtils";
import ToggleSwitch from "views/ToggleSwitch";

type Props = {
  appRouter: any;
};

type State = {
  billingPeriod: "month" | "year";
};

class CheckoutPageView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      billingPeriod: "year",
    };
  }

  async componentDidMount() {
    console.log("CheckoutPageView mounted");
    if (await readlangClient.isLocalMode()) {
      signupModal.show({
        stores: stores,
        heading: "Sign in to Readlang",
        cancelCallback: () => {
          this.props.appRouter.navigate("/account", { trigger: true });
        },
      });
    }
  }

  render() {
    console.log("rendering CheckoutPageView");
    $("head title").text("Checkout");

    const learningLanguage = stores.user.currentLearningLanguage.value;
    const isLowResource =
      learningLanguage && languageMap.isLowResource(learningLanguage);

    const emailVerificationStatus = urlUtils.getUrlVar(
      "emailVerificationStatus"
    );
    const justVerifiedEmail = urlUtils.getUrlVar("justVerifiedEmail");
    const userID = stores.user.id.value;

    $('.plan input[name="custom"]').val(userID);

    const openCheckout = function (
      billingPeriod: "month" | "year",
      plan: "premium" | "premiumPlus"
    ) {
      if (!userID) {
        alert("No userID");
        return;
      }

      postNavigate(`/api/createCheckoutSession/${billingPeriod}?plan=${plan}`);
    };

    const yearlyPriceDisplay = (
      yearlyPrice: number,
      monthlyEquivalent: number
    ) => (
      <>
        <div class="price">
          ${monthlyEquivalent}
          <small> / month</small>
        </div>
        <div class="yearlyCostBreakdown">
          Yearly cost
          <div class="yearlyCost">US${yearlyPrice}</div>
        </div>
      </>
    );

    const premiumPlan = (
      <div class="plan2">
        <div class="billingPeriod">
          Premium <small>(most&nbsp;popular)</small>
        </div>
        {this.state.billingPeriod === "year" ? (
          yearlyPriceDisplay(48, 4)
        ) : (
          <div class="price">
            $6
            <small> / month</small>
          </div>
        )}

        <button
          class="payNowButton"
          onClick={() => {
            openCheckout(this.state.billingPeriod, "premium");
          }}
        >
          Pay now
        </button>
      </div>
    );

    const premiumPlusPlan = (
      <div class="plan2">
        {/* <div class="saveNotice">Save $24</div> */}
        <div class="billingPeriod">
          Premium Plus{" "}
          <small>(better&nbsp;AI&nbsp;+&nbsp;more&nbsp;audio)</small>
        </div>
        {this.state.billingPeriod === "year" ? (
          yearlyPriceDisplay(120, 10)
        ) : (
          <div class="price">
            $15
            <small> / month</small>
          </div>
        )}

        <button
          class="payNowButton secondary"
          onClick={() => {
            openCheckout(this.state.billingPeriod, "premiumPlus");
          }}
        >
          Pay now
        </button>
      </div>
    );

    const audioSupportedLanguages = languageMap.list
      .filter((language) => languageMap.map[language.code]?.whisper === true)
      .map((language) => language.english)
      .sort()
      .join(", ");

    const audioUploadMessage = (
      <>
        <strong>
          <sup>1</sup> Audio upload and transcription
        </strong>{" "}
        is available for: {audioSupportedLanguages}.
      </>
    );

    const writtenConversationMessage = (
      <>
        <strong>
          <sup>2</sup> Written Conversation
        </strong>{" "}
        is available in all languages <strong>except for</strong>{" "}
        {languageMap.list
          .filter(
            (language) => !languageMap.languageSupportsChat(language.code)
          )
          .map((language) => language.english)
          .join(", ")}
        .
      </>
    );
    const voiceSupportedLanguages = languageMap.list
      .filter((language) =>
        ttsVoiceCatalog.isAzureSupportedLanguage(language.code)
      )
      .map((language) => language.english)
      .sort()
      .join(", ");

    const speakingModeMessage = (
      <>
        <strong>
          <sup>3</sup> High quality Readlang voices
        </strong>{" "}
        are generated by Readlang, so they sound consistent across your devices.
        They are available for: {voiceSupportedLanguages}.
      </>
    );
    const cancelMessage = (
      <>
        <strong>Readlang Premium</strong> is a subscription service. You can{" "}
        <strong>cancel at any time</strong>, and you won't be charged again.
      </>
    );
    const languagesMessage = (
      <>
        <strong> One Premium subscription</strong> will work across all
        supported languages, so you can{" "}
        <strong>study multiple languages at the same time</strong>.
      </>
    );
    const switchPlansMessage = (
      <>
        After subscribing you can{" "}
        <strong>switch between Premium and Premium Plus plans</strong>. Any
        remaining days on your current subscription will be automatically
        adjusted and applied to the new plan using a prorated calculation. e.g.
        if you have 8 days left on your Premium Plus plan and decide to
        downgrade, this will turn into 20 days on the Premium plan (8 x 2.5).
      </>
    );

    return (
      <>
        <div class="checkoutPage2Contents">
          <div id="header">
            <div class="bookColumn">
              <div class="topBar">
                <a href="/" class="logoLink">
                  <img class="rabbitLogo" src="/content/rabbitLogo.svg" />
                  Readlang
                </a>
              </div>
              <h1>Become bilingual faster!</h1>
              <div class="toggle-container">
                <ToggleSwitch
                  stateOne="monthly billing"
                  stateTwo="yearly billing"
                  onToggle={(selected) => {
                    console.log("selected: ", selected);
                    this.setState({
                      billingPeriod:
                        selected === "monthly billing" ? "month" : "year",
                    });
                  }}
                />
                <div
                  class={`discount-pill ${this.state.billingPeriod === "year" ? "active" : ""}`}
                >
                  -33%
                </div>
              </div>
            </div>
          </div>

          <div class="planContainer2">
            {emailVerificationStatus === "sent" ? (
              <div
                id="verificationNoticeSent"
                class="verificationNotice callout"
              >
                IMPORTANT: We've just sent an email to{" "}
                <span class="email"></span> with a link to verify your email
                address. Please click the link in the email to continue.
              </div>
            ) : emailVerificationStatus === "sentPreviously" ? (
              <div
                id="verificationNoticeSentPreviously"
                class="verificationNotice callout"
              >
                IMPORTANT: We previously sent an email to{" "}
                <span class="email"></span> with a link to verify your email
                address. Please click that link in the email to continue.
              </div>
            ) : justVerifiedEmail === "true" ? (
              <div id="justVerifiedEmail" class="callout">
                Thanks for verifying your email address!
                <br />
                Choose one of the options below to continue.
              </div>
            ) : null}
            {isLowResource && (
              <div class="callout lowResourceCallout">
                <strong>
                  Learning {languageMap.map[learningLanguage].english}?
                </strong>{" "}
                We recommend <strong>Premium Plus</strong> for this language.
                Its advanced AI model provides significantly better translations
                and explanations for languages with limited AI training data.
                You can switch plans at any time with pro-rated pricing.
              </div>
            )}
            {/* {deviceDetect.isSmall() ? (
              <>
                {premiumPlusPlan}
                {premiumPlan}
              </>
            ) : ( */}
            <>
              {premiumPlan}
              {premiumPlusPlan}
            </>
            {/* )} */}

            <h2 class="noMobile">What's included in each plan?</h2>
            <h2 class="mobileOnly">What's included in Premium?</h2>

            <div class="mobileFeatureList2">
              <table class="mobileFeatureListTable">
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Unlimited Phrase Translations</td>
                </tr>
                <tr>
                  <td></td>
                  <td class="subtle">(instead of 10 / day)</td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Phrases of up to 12 words long</td>
                </tr>
                <tr>
                  <td></td>
                  <td class="subtle">(instead of 6)</td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Unlimited Context Explanations</td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Context-Aware Translations</td>
                </tr>
                <tr>
                  <td></td>
                  <td class="subtle">(instead of 10 / day)</td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    Audio upload and transcription<sup>1</sup> (10 hours
                    storage)
                  </td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    Written conversations<sup>2</sup> (2 per day)
                  </td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Generate stories with the Story Bot</td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Practice or export flashcards from specific books</td>
                </tr>

                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    Auto Highlighting, Shelves,{" "}
                    <strong>high quality voices</strong> for speaking words and
                    phrases<sup>3</sup>
                  </td>
                </tr>
              </table>
            </div>

            <h2 class="mobileOnly">What's included in Premium Plus?</h2>

            <div class="mobileFeatureList2">
              <table class="mobileFeatureListTable">
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>Everything from Premium</td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    Better AI model powering context-aware translations,
                    explanations and summaries
                  </td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    200 hours of audio storage<sup>1</sup>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    <strong>High quality voices</strong> for speaking whole
                    texts<sup>3</sup>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    Unlimited written conversations<sup>2</sup>
                  </td>
                </tr>
              </table>
            </div>

            <div class="mobileMessages">
              <p>{audioUploadMessage}</p>
              <p>{writtenConversationMessage}</p>
              <p>{speakingModeMessage}</p>
              <p class="larger">{cancelMessage}</p>
              <p class="larger">{languagesMessage}</p>
              <p class="larger">{switchPlansMessage}</p>
            </div>

            <div class="featureTable noMobile">
              <table>
                <tr>
                  <th>
                    <h3>Features</h3>
                  </th>
                  <th>
                    <h3>Free</h3>
                  </th>
                  <th>
                    <h3>Premium</h3>
                  </th>
                  <th>
                    <h3>Premium Plus</h3>
                  </th>
                </tr>
                <tr>
                  <td colspan={4}>
                    <div class="dividerLine"></div>
                  </td>
                </tr>
                <tr>
                  <td>Single word translations</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Text uploads</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Flashcard practices</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Maximum words translated per phrase</td>
                  <td>6 words</td>
                  <td>12 words</td>
                  <td>12 words</td>
                </tr>
                <tr>
                  <td>Phrase translations</td>
                  <td>10 / day</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Context explanations</td>
                  <td>10 / day</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Word highlighting</td>
                  <td>200 words in total</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Context-aware translations</td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                </tr>
                <tr>
                  <td>
                    Audio upload and transcription<sup>1</sup>
                  </td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>
                    10 hours
                    <br />
                    storage
                  </td>
                  <td>
                    200 hours
                    <br />
                    storage
                  </td>
                </tr>
                <tr>
                  <td>
                    Written conversations<sup>2</sup>
                  </td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>2 / day</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Generate stories with the Story Bot</td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                </tr>
                <tr>
                  <td>Practice words from specific texts</td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                </tr>
                <tr>
                  <td>
                    High quality Readlang voices<sup>3</sup>
                  </td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>Words and phrases</td>
                  <td>Whole texts</td>
                </tr>
                <tr>
                  <td>Shelves</td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                  <td>
                    <div class="checkout-check-circle"></div>
                  </td>
                </tr>
                <tr>
                  <td>Streak repair</td>
                  <td>
                    <div class="checkout-x-circle"></div>
                  </td>
                  <td>2 / month</td>
                  <td>2 / month</td>
                </tr>
                <tr>
                  <td>
                    AI model
                    <br />
                    <small>
                      (powers context explanations, context-aware translations,
                      review summaries)
                    </small>
                  </td>
                  <td>
                    Standard
                    <br />
                    <small>(limited&nbsp;usage)</small>
                  </td>
                  <td>
                    Standard
                    <br />
                    &nbsp;
                  </td>
                  <td>
                    Advanced
                    <br />
                    &nbsp;
                  </td>
                </tr>
                <tr>
                  <td colspan={4}>
                    <div class="dividerLine"></div>
                  </td>
                </tr>
              </table>
              <p class="tableFooterInfo">
                <p>{audioUploadMessage}</p>
                <p>{writtenConversationMessage}</p>
                <p>{speakingModeMessage}</p>
                <div class="tableFooterCallout">
                  <p>{cancelMessage}</p>
                  <p>{languagesMessage}</p>
                  <p>{switchPlansMessage}</p>
                </div>
              </p>
            </div>
          </div>
        </div>
        <div id="footer">
          <div class="bookColumn">
            <div class="row">
              <div id="socialLinks">
                <p>Connect with us</p>
                <ul>
                  <li>
                    <a href="https://www.facebook.com/readlang" class="logo">
                      <i class="icon-facebook-sign"></i>
                    </a>
                    <a href="https://twitter.com/Readlang" class="logo">
                      𝕏
                    </a>
                  </li>
                </ul>
              </div>
              <div class="col2_3">
                <ul>
                  <li>
                    <a href="https://www.youtube.com/watch?v=wF31VqZMqE8&index=2&list=PLIGa-eWCssj_dcE9GXkpLSFB9t8qZyZn2">
                      Tutorial Videos
                    </a>
                  </li>
                  <li>
                    <a href="https://forum.readlang.com/">
                      Help &amp; Feedback
                    </a>
                  </li>
                  <li>
                    <a href="http://blog.readlang.com">Readlang Blog</a>
                  </li>
                  <li>
                    <a href="/webReader">Web Reader Extension & Bookmarklet</a>
                  </li>
                  <li>
                    <a href="https://forum.readlang.com/t/install-readlang-on-your-iphone-ipad-or-android-home-screen/45">
                      Readlang for iOS and Android
                    </a>
                  </li>
                  <li>
                    <a data-bypass="true" href="/landing-page">
                      Landing Page
                    </a>
                  </li>
                  <li>
                    <a href="/privacyPolicy">Privacy Policy</a>
                  </li>
                  <li>
                    <a href="/termsOfService">Terms of Service</a>
                  </li>
                  <li>
                    <a href="/account">Pricing</a>
                  </li>
                  <li>
                    <a href="http://blog.readlang.com/about">About & Contact</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default CheckoutPageView;
